/**
 * Asset Grid Editor - New Implementation
 *
 * Visual editor for positioning zones on an asset diagram.
 * Simplified without language dictionary - uses direct field values.
 */
import React, { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Box, Paper, Typography, CircularProgress, Alert, Chip } from "@mui/material";
import { getAssetViewImage } from "../../../../../services-px/configApi";
import { updateZonePosition } from "../redux/slice";

/**
 * AssetGridEditor - Visual editor for positioning zones on an asset diagram
 */
const AssetGridEditor = ({
	layout,
	layoutIndex,
	inspectionTypes,
	highlightedZoneIndex = null,
	onZoneHover,
}) => {
	const { messages } = useIntl();
	const dispatch = useDispatch();
	const [assetBackground, setAssetBackground] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [backgroundSize, setBackgroundSize] = useState({ width: 0, height: 0 });
	const [gridItems, setGridItems] = useState([]);
	const [selectedInspectionTypes, setSelectedInspectionTypes] = useState([]);
	const [hoveredZoneIndex, setHoveredZoneIndex] = useState(null);
	const [draggingZoneIndex, setDraggingZoneIndex] = useState(null);
	const [dragHoverPosition, setDragHoverPosition] = useState(null);
	const svgRef = useRef(null);
	const containerRef = useRef(null);

	// Grid dimensions from layout
	const gridWidth = layout?.assetViewGrid?.[0] || 7;
	const gridHeight = layout?.assetViewGrid?.[1] || 7;

	// Constants for zone box sizing
	const MAX_ZONE_BOX_SIZE = 28;
	const MAX_ZONE_DOT_SIZE = 24;

	// Load asset background image when layout changes
	useEffect(() => {
		const loadAssetBackground = async () => {
			if (!layout?.assetViewId) {
				setAssetBackground(null);
				setBackgroundSize({ width: 0, height: 0 });
				return;
			}

			try {
				setLoading(true);
				setError(null);
				// Clear previous state before loading new image
				setAssetBackground(null);
				setBackgroundSize({ width: 0, height: 0 });

				const svgContent = await getAssetViewImage(layout.assetViewId);
				setAssetBackground(svgContent);
			} catch (err) {
				let errorMsg = messages.failedToLoadAssetImage || "Failed to load asset image";
				if (err.response?.status === 404) {
					errorMsg =
						messages.assetViewImageNotFound || "Asset view image not found. The image may need to be uploaded for this layout.";
				} else if (err.response?.status) {
					errorMsg = `${messages.failedToLoadAssetImage || "Failed to load asset image"}: ${err.response.status} ${err.response.statusText}`;
				} else {
					errorMsg = `${messages.failedToLoadAssetImage || "Failed to load asset image"}: ${err.message}`;
				}

				setError(errorMsg);
				setAssetBackground(null);
			} finally {
				setLoading(false);
			}
		};

		loadAssetBackground();
	}, [layout?.assetViewId]);

	// Measure SVG dimensions after it loads
	useEffect(() => {
		if (assetBackground && svgRef.current) {
			setTimeout(() => {
				const svgElement = svgRef.current?.querySelector("svg");

				if (svgElement) {
					const bbox = svgElement.getBBox();
					const computedWidth = svgElement.clientWidth || bbox.width;
					const computedHeight = svgElement.clientHeight || bbox.height;

					setBackgroundSize({
						width: computedWidth,
						height: computedHeight,
					});
				}
			}, 100);
		}
	}, [assetBackground]);

	// Filter inspectionTypes by selected (show all)
	useEffect(() => {
		if (inspectionTypes && inspectionTypes.length > 0) {
			// Extract both typeId and detailType values - zones may reference either
			// (zones saved before typeId was assigned will have detailType strings)
			const allInspectionTypeIds = new Set();
			inspectionTypes.forEach((it) => {
				if (it.typeId) allInspectionTypeIds.add(it.typeId);
				if (it.detailType) allInspectionTypeIds.add(it.detailType);
			});
			setSelectedInspectionTypes(Array.from(allInspectionTypeIds));
		}
	}, [inspectionTypes]);

	// Build grid items from zones
	useEffect(() => {
		if (
			!layout ||
			!backgroundSize.width ||
			!backgroundSize.height ||
			selectedInspectionTypes.length === 0
		) {
			return;
		}

		const calculatedZoneBoxHeight = Math.ceil(backgroundSize.height / gridHeight);
		const zoneBoxHeight = Math.min(calculatedZoneBoxHeight, MAX_ZONE_BOX_SIZE);
		const zoneBoxWidth = MAX_ZONE_BOX_SIZE;
		const zoneBoxActiveSize = Math.min(zoneBoxHeight, MAX_ZONE_DOT_SIZE);

		// Initialize grid with empty cells
		const items = [];
		for (let i = 0; i < gridHeight * gridWidth; i++) {
			const positionY = Math.floor(i / gridWidth);
			const positionX = i % gridWidth;
			items.push({
				gridIndex: i,
				positionX,
				positionY,
				active: false,
				isDuplicate: false,
				zoneBoxHeight,
				zoneBoxWidth,
				zoneBoxActiveSize,
			});
		}

		// Place zones on the grid
		layout.configZones?.forEach((zone, zoneIndex) => {
			const positionX = zone.assetViewLocation?.[0];
			const positionY = zone.assetViewLocation?.[1];

			if (positionX == null || positionY == null) {
				return;
			}

			if (positionX >= gridWidth || positionY >= gridHeight) {
				return;
			}

			// Check if this zone has any of the selected inspection types
			// Zones with no inspection types should still be shown
			// Zones with only non-existent inspection types should also be shown (treated as "None")
			const hasNoInspectionTypes = !zone.inspectionTypes || zone.inspectionTypes.length === 0;
			const hasMatchingInspectionType = zone.inspectionTypes?.some((typeId) =>
				selectedInspectionTypes.includes(typeId)
			);
			// Check if zone has inspection types but none of them exist in the config
			const hasOnlyNonExistentTypes = !hasNoInspectionTypes && !hasMatchingInspectionType;

			// Show zone if: no inspection types, OR has matching types, OR has only non-existent types
			const shouldShowZone = hasNoInspectionTypes || hasMatchingInspectionType || hasOnlyNonExistentTypes;

			if (!shouldShowZone) {
				return;
			}

			const gridIndex = positionY * gridWidth + positionX;

			// Get zone name - DIRECT field access
			const zoneName = zone.tag || "Unnamed Zone";
			const zoneType = (zone.tagType || "Zone").charAt(0).toUpperCase();
			const tagNumber = zone.tagNumber ?? "?";
			const abbreviatedName = `${zoneType}${tagNumber}`;
			const fullName = `${zoneName} (${abbreviatedName})`;

			// Handle multiple zones at same position
			if (items[gridIndex].active) {
				items[gridIndex].isDuplicate = true;
				items[gridIndex].name += `, ${abbreviatedName}`;
				items[gridIndex].configZoneName += `,\n${fullName}`;
				if (!items[gridIndex].zoneIndices) {
					items[gridIndex].zoneIndices = [items[gridIndex].zoneIndex];
				}
				items[gridIndex].zoneIndices.push(zoneIndex);
			} else {
				items[gridIndex] = {
					...items[gridIndex],
					active: true,
					name: abbreviatedName,
					configZoneName: fullName,
					zone,
					zoneIndex,
				};
			}
		});

		setGridItems(items);
	}, [layout, backgroundSize, selectedInspectionTypes, gridWidth, gridHeight]);

	// Calculate grid cell width based on background width
	const calculateZoneBoxWidth = (item) => {
		if (!backgroundSize.width) return item.zoneBoxWidth;
		return backgroundSize.width / gridWidth;
	};

	// Handle drag start
	const handleDragStart = (e, zoneIndex) => {
		e.stopPropagation();
		setDraggingZoneIndex(zoneIndex);
	};

	// Handle drag over container
	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		e.dataTransfer.dropEffect = "move";

		if (draggingZoneIndex === null || !containerRef.current || !svgRef.current)
			return;

		const svgElement = svgRef.current.querySelector("svg");
		if (!svgElement) return;

		const svgRect = svgElement.getBoundingClientRect();
		const cellWidth = svgRect.width / gridWidth;
		const cellHeight = svgRect.height / gridHeight;

		const relativeX = e.clientX - svgRect.left;
		const relativeY = e.clientY - svgRect.top;

		const newX = Math.max(0, Math.min(gridWidth - 1, Math.floor(relativeX / cellWidth)));
		const newY = Math.max(
			0,
			Math.min(gridHeight - 1, Math.floor(relativeY / cellHeight))
		);

		setDragHoverPosition({ x: newX, y: newY });
	};

	// Handle drop
	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();

		if (draggingZoneIndex === null || layoutIndex === null || !svgRef.current) {
			return;
		}

		const svgElement = svgRef.current.querySelector("svg");
		if (!svgElement) return;

		const svgRect = svgElement.getBoundingClientRect();
		const cellWidth = svgRect.width / gridWidth;
		const cellHeight = svgRect.height / gridHeight;

		const relativeX = e.clientX - svgRect.left;
		const relativeY = e.clientY - svgRect.top;

		const newX = Math.max(0, Math.min(gridWidth - 1, Math.floor(relativeX / cellWidth)));
		const newY = Math.max(
			0,
			Math.min(gridHeight - 1, Math.floor(relativeY / cellHeight))
		);

		dispatch(
			updateZonePosition({
				layoutIndex,
				zoneIndex: draggingZoneIndex,
				newPosition: [newX, newY],
			})
		);

		setDraggingZoneIndex(null);
		setDragHoverPosition(null);
	};

	// Handle drag end
	const handleDragEnd = () => {
		setDraggingZoneIndex(null);
		setDragHoverPosition(null);
	};

	if (!layout) {
		return null;
	}

	if (!layout.assetViewId) {
		return (
			<Box>
				<Box sx={{ pt: 2, mb: 2 }}>
					<Typography variant="h6">{messages.assetMapView || "Asset map view"}</Typography>
				</Box>
				<Paper sx={{ p: 3, mb: 3 }}>
					<Alert severity="info">
						{messages.noAssetViewImageConfigured || "No asset view image configured for this layout. To display zones visually, an asset view image needs to be uploaded."}
					</Alert>
				</Paper>
			</Box>
		);
	}

	return (
		<Box>
			<Box sx={{ pt: 2, mb: 2 }}>
				<Typography variant="h6">{messages.assetMapView || "Asset map view"}</Typography>
			</Box>
			<Paper elevation={2} sx={{ p: 2, mb: 3 }}>
				<style>
					{`
						@keyframes pulse {
							0%, 100% {
								transform: scale(1.3);
							}
							50% {
								transform: scale(1.5);
							}
						}
					`}
				</style>
				{error && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						<Typography variant="body2" sx={{ mb: 1 }}>
							{error}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							AssetViewId: {layout.assetViewId}
						</Typography>
					</Alert>
				)}

				{loading && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: 200,
						}}
					>
						<CircularProgress />
					</Box>
				)}

				{assetBackground && !loading && (
					<Box sx={{ position: "relative", display: "block", overflow: "hidden" }}>
						{/* SVG Background */}
						<Box
							ref={svgRef}
							dangerouslySetInnerHTML={{ __html: assetBackground }}
							sx={{
								"& svg": {
									maxWidth: "100%",
									height: "auto",
									display: "block",
								},
							}}
						/>

						{/* Zone Grid Overlay */}
						{backgroundSize.width > 0 &&
							backgroundSize.height > 0 &&
							gridItems.length > 0 && (
								<Box
									ref={containerRef}
									onDragOver={handleDragOver}
									onDrop={handleDrop}
									sx={{
										position: "absolute",
										top: 0,
										left: 0,
										width: backgroundSize.width,
										height: backgroundSize.height,
										pointerEvents: "auto",
										overflow: "visible",
									}}
								>
									{/* Drop Target Grid Overlay - Show during drag */}
									{draggingZoneIndex !== null && dragHoverPosition && (
										<svg
											style={{
												position: "absolute",
												top: 0,
												left: 0,
												width: "100%",
												height: "100%",
												pointerEvents: "none",
												zIndex: 5,
											}}
											viewBox={`0 0 ${backgroundSize.width} ${backgroundSize.height}`}
											preserveAspectRatio="xMidYMid meet"
										>
											<rect
												x="0"
												y="0"
												width={backgroundSize.width}
												height={backgroundSize.height}
												fill="none"
												stroke="rgba(0, 0, 0, 0.6)"
												strokeWidth="2"
											/>
											{Array.from({ length: gridHeight }).map((_, rowIndex) =>
												Array.from({ length: gridWidth }).map((_, colIndex) => {
													const cellWidth = backgroundSize.width / gridWidth;
													const cellHeight = backgroundSize.height / gridHeight;
													const x = colIndex * cellWidth;
													const y = rowIndex * cellHeight;

													const isHovered =
														dragHoverPosition.x === colIndex &&
														dragHoverPosition.y === rowIndex;
													const distance = Math.max(
														Math.abs(dragHoverPosition.x - colIndex),
														Math.abs(dragHoverPosition.y - rowIndex)
													);
													const isNearby = distance === 1;

													return (
														<rect
															key={`${rowIndex}-${colIndex}`}
															x={x}
															y={y}
															width={cellWidth}
															height={cellHeight}
															fill={
																isHovered
																	? "rgba(25, 118, 210, 0.3)"
																	: isNearby
																	? "rgba(25, 118, 210, 0.15)"
																	: "rgba(0, 0, 0, 0.08)"
															}
															stroke={
																isHovered
																	? "rgba(25, 118, 210, 0.8)"
																	: "rgba(0, 0, 0, 0.4)"
															}
															strokeWidth={isHovered ? 2 : 1}
															strokeDasharray={isHovered ? "none" : "4 2"}
														/>
													);
												})
											)}
										</svg>
									)}

									{gridItems.map((item, index) => {
										if (!item.active) return null;

										const svgElement = svgRef.current?.querySelector("svg");
										const svgRect = svgElement?.getBoundingClientRect();
										const cellWidth = svgRect
											? svgRect.width / gridWidth
											: calculateZoneBoxWidth(item);
										const cellHeight = svgRect
											? svgRect.height / gridHeight
											: item.zoneBoxHeight;
										const left = item.positionX * cellWidth;
										const top = item.positionY * cellHeight;

										const isHighlighted = item.isDuplicate
											? item.zoneIndices?.includes(highlightedZoneIndex)
											: item.zoneIndex === highlightedZoneIndex;
										const isHovered = hoveredZoneIndex === index;
										const isDragging = draggingZoneIndex === item.zoneIndex;

										return (
											<Box
												key={item.gridIndex}
												draggable={!item.isDuplicate}
												onDragStart={(e) => handleDragStart(e, item.zoneIndex)}
												onDragEnd={handleDragEnd}
												onMouseEnter={() => {
													setHoveredZoneIndex(index);
													if (onZoneHover) {
														// For duplicates, pass all zone indices to highlight all rows
														onZoneHover(item.isDuplicate ? item.zoneIndices : item.zoneIndex);
													}
												}}
												onMouseLeave={() => {
													setHoveredZoneIndex(null);
													if (onZoneHover) {
														onZoneHover(null);
													}
												}}
												sx={{
													position: "absolute",
													left: `${left}px`,
													top: `${top}px`,
													width: `${cellWidth}px`,
													height: `${cellHeight}px`,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													pointerEvents: "auto",
													cursor: item.isDuplicate ? "not-allowed" : "move",
													// Disable transitions for ALL zones during drag to prevent ghosting
													transition: draggingZoneIndex !== null ? "none" : "all 0.2s ease",
													zIndex: isDragging ? 100 : isHighlighted ? 10 : 1,
													opacity: isDragging ? 0.5 : 1,
													...(isHighlighted &&
														!isDragging && {
															animation: "pulse 1.5s ease-in-out infinite",
														}),
												}}
											>
												<Chip
													label={item.isDuplicate ? "M" : item.name.slice(1)}
													title={
														item.isDuplicate
															? `${item.configZoneName}\n\nMultiple zones at same position - edit individual zones to change their grid position`
															: `${item.configZoneName}\n\nDrag to reposition`
													}
													size="small"
													color={
														item.isDuplicate
															? "warning"
															: isHighlighted || isHovered
															? "secondary"
															: "primary"
													}
													sx={{
														width: `${item.zoneBoxActiveSize}px`,
														height: `${item.zoneBoxActiveSize}px`,
														fontSize: "10px",
														fontWeight: "bold",
														cursor: item.isDuplicate ? "not-allowed" : "move",
														transition: draggingZoneIndex !== null ? "none" : "all 0.2s ease",
														opacity: isDragging ? 0.8 : isHighlighted ? 1 : 0.7,
														...(isHighlighted &&
															!isDragging && {
																transform: "scale(1.5)",
																boxShadow: "0 0 16px rgba(156, 39, 176, 0.8)",
															}),
														...(isHovered &&
															!isHighlighted &&
															!isDragging && {
																transform: "scale(1.15)",
																boxShadow: "0 0 8px rgba(25, 118, 210, 0.5)",
															}),
														"& .MuiChip-label": {
															padding: 0,
															overflow: "visible",
														},
														"&, & *": {
															cursor: item.isDuplicate ? "not-allowed !important" : "move !important",
														},
													}}
												/>
												{item.isDuplicate && (
													<Box
														sx={{
															position: "absolute",
															top: isHighlighted ? "calc(50% + 28px)" : isHovered ? "calc(50% + 24px)" : "calc(50% + 20px)",
															left: "50%",
															transform: "translateX(-50%)",
															whiteSpace: "nowrap",
															fontSize: "9px",
															bgcolor: "warning.main",
															color: "white",
															px: 0.5,
															borderRadius: 0.5,
															transition: "top 0.2s ease",
														}}
													>
														Multiple ({item.name})
													</Box>
												)}
											</Box>
										);
									})}
								</Box>
							)}
					</Box>
				)}
			</Paper>
		</Box>
	);
};

export default AssetGridEditor;
